// PackageManagement.js - Scans Beheer (NIEUWE VERSIE)
import React, { useState, useEffect } from 'react';
import { 
  getAllUsers, 
  getPackageStatistics,
  addScans,
  extendValidity
} from '../utils/adminPackageHelpers';
import './PackageManagement.css';

const PackageManagement = () => {
  // State management
  const [users, setUsers] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    name: '',
    email: '',
    status: 'all' // all, active, expired, inactive
  });

  // Modal states
  const [selectedUser, setSelectedUser] = useState(null);
  const [showAddScansModal, setShowAddScansModal] = useState(false);
  const [showExtendModal, setShowExtendModal] = useState(false);

  // Form states
  const [addScansAmount, setAddScansAmount] = useState('');
  const [extendMonths, setExtendMonths] = useState(6);
  const [customMonths, setCustomMonths] = useState('');

  const [message, setMessage] = useState({ type: '', text: '' });

  // Load initial data
  useEffect(() => {
    loadUsers();
    loadStatistics();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const userData = await getAllUsers(filters);
      setUsers(userData);
    } catch (error) {
      showMessage('error', 'Fout bij laden gebruikers: ' + error.message);
    }
    setLoading(false);
  };

  const loadStatistics = async () => {
    try {
      const stats = await getPackageStatistics();
      setStatistics(stats);
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  // Filter users based on search and status
  const getFilteredUsers = () => {
    return users.filter(user => {
      const matchesName = !filters.name || 
        (user.name || '').toLowerCase().includes(filters.name.toLowerCase());
      const matchesEmail = !filters.email || 
        user.email.toLowerCase().includes(filters.email.toLowerCase());
      
      let matchesStatus = true;
      if (filters.status === 'active') {
        matchesStatus = user.hasActivePackage && user.packageStatus.daysLeft > 0;
      } else if (filters.status === 'expired') {
        matchesStatus = user.hasActivePackage && user.packageStatus.daysLeft <= 0;
      } else if (filters.status === 'inactive') {
        matchesStatus = !user.hasActivePackage || user.packageStatus.scansTotal === 0;
      }
      
      return matchesName && matchesEmail && matchesStatus;
    });
  };

  // Get status for user
  const getUserStatus = (user) => {
    if (!user.hasActivePackage || user.packageStatus.scansTotal === 0) {
      return { label: 'INACTIEF', class: 'inactive', icon: '⚪' };
    }
    
    const daysLeft = user.packageStatus.daysLeft || 0;
    
    if (daysLeft <= 0) {
      return { label: 'VERLOPEN', class: 'expired', icon: '🔴' };
    } else if (daysLeft <= 30) {
      return { label: 'BIJNA VERLOPEN', class: 'expiring', icon: '🟡' };
    } else {
      return { label: 'ACTIEF', class: 'active', icon: '🟢' };
    }
  };

  // Calculate new expiry date for adding scans
  const calculateNewExpiryForScans = (user) => {
    const today = new Date();
    const sixMonthsLater = new Date(today);
    sixMonthsLater.setMonth(sixMonthsLater.getMonth() + 6);
    
    if (user.packageExpiresAt) {
      const currentExpiry = new Date(user.packageExpiresAt.seconds * 1000);
      return currentExpiry > sixMonthsLater ? currentExpiry : sixMonthsLater;
    }
    
    return sixMonthsLater;
  };

  // Calculate new expiry date for extension
  const calculateNewExpiryForExtend = (user, months) => {
    if (user.packageExpiresAt) {
      const currentExpiry = new Date(user.packageExpiresAt.seconds * 1000);
      const newExpiry = new Date(currentExpiry);
      newExpiry.setMonth(newExpiry.getMonth() + months);
      return newExpiry;
    }
    
    // If no expiry yet, start from today
    const newExpiry = new Date();
    newExpiry.setMonth(newExpiry.getMonth() + months);
    return newExpiry;
  };

  // Open Add Scans Modal
  const openAddScansModal = (user) => {
    setSelectedUser(user);
    setAddScansAmount('');
    setShowAddScansModal(true);
  };

  // Close Add Scans Modal
  const closeAddScansModal = () => {
    setSelectedUser(null);
    setAddScansAmount('');
    setShowAddScansModal(false);
  };

  // Handle Add Scans
  const handleAddScans = async () => {
    if (!selectedUser || !addScansAmount || addScansAmount <= 0) {
      showMessage('error', 'Vul een geldig aantal scans in');
      return;
    }

    setLoading(true);
    try {
      const result = await addScans(selectedUser.email, parseInt(addScansAmount));
      
      if (result.success) {
        showMessage('success', result.message);
        closeAddScansModal();
        await loadUsers();
      } else {
        showMessage('error', result.message);
      }
      
    } catch (error) {
      showMessage('error', 'Fout bij toevoegen scans: ' + error.message);
    }
    setLoading(false);
  };

  // Open Extend Modal
  const openExtendModal = (user) => {
    setSelectedUser(user);
    setExtendMonths(6);
    setCustomMonths('');
    setShowExtendModal(true);
  };

  // Close Extend Modal
  const closeExtendModal = () => {
    setSelectedUser(null);
    setExtendMonths(6);
    setCustomMonths('');
    setShowExtendModal(false);
  };

  // Handle Extend
  const handleExtend = async () => {
    if (!selectedUser) return;

    const months = extendMonths === 'custom' ? parseInt(customMonths) : extendMonths;
    
    if (!months || months <= 0) {
      showMessage('error', 'Vul een geldig aantal maanden in');
      return;
    }

    setLoading(true);
    try {
      const result = await extendValidity(selectedUser.email, months);
      
      if (result.success) {
        showMessage('success', result.message);
        closeExtendModal();
        await loadUsers();
      } else {
        showMessage('error', result.message);
      }
      
    } catch (error) {
      showMessage('error', 'Fout bij verlengen: ' + error.message);
    }
    setLoading(false);
  };

  // Apply filters
  const handleApplyFilters = () => {
    loadUsers();
  };

  // Reset filters
  const handleResetFilters = () => {
    setFilters({
      name: '',
      email: '',
      status: 'all'
    });
  };

  // Render Overview (Statistics)
  const renderOverview = () => (
    <div className="package-overview">
      <h3>Scans Overzicht</h3>
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{statistics.totalUsers || 0}</div>
          <div className="stat-label">Totaal Gebruikers</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{statistics.activePackages || 0}</div>
          <div className="stat-label">Actieve Scans</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{statistics.totalScansDistributed || 0}</div>
          <div className="stat-label">Totaal Scans</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{statistics.totalScansUsed || 0}</div>
          <div className="stat-label">Scans Gebruikt</div>
        </div>
      </div>
    </div>
  );

  // Render Users Table
  const renderUsersTable = () => {
    const filteredUsers = getFilteredUsers();

    if (loading) {
      return <div className="loading">Laden...</div>;
    }

    if (filteredUsers.length === 0) {
      return <div className="loading">Geen gebruikers gevonden</div>;
    }

    return (
      <div className="users-table">
        <table>
          <thead>
            <tr>
              <th>Status</th>
              <th>Naam</th>
              <th>Email</th>
              <th>Totaal</th>
              <th>Gebruikt</th>
              <th>Beschikbaar</th>
              <th>Vervaldatum</th>
              <th>Acties</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => {
              const status = getUserStatus(user);
              const scansTotal = user.packageStatus?.scansTotal || 0;
              const scansUsed = user.packageStatus?.scansUsed || 0;
              const scansLeft = user.packageStatus?.scansLeft || 0;
              const expiryDate = user.packageExpiresAt 
                ? new Date(user.packageExpiresAt.seconds * 1000).toLocaleDateString('nl-NL')
                : '-';

              return (
                <tr key={user.email}>
                  <td>
                    <span className={`status-badge ${status.class}`}>
                      {status.icon} {status.label}
                    </span>
                  </td>
                  <td>{user.name || '-'}</td>
                  <td>{user.email}</td>
                  <td>{scansTotal}</td>
                  <td>{scansUsed}</td>
                  <td><strong>{scansLeft}</strong></td>
                  <td>{expiryDate}</td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="btn btn-primary btn-sm"
                        onClick={() => openAddScansModal(user)}
                        disabled={loading}
                      >
                        + Scans
                      </button>
                      <button 
                        className="btn btn-secondary btn-sm"
                        onClick={() => openExtendModal(user)}
                        disabled={loading}
                      >
                        ⏱ Verlengen
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // Render Add Scans Modal
  const renderAddScansModal = () => {
    if (!showAddScansModal || !selectedUser) return null;

    const currentTotal = selectedUser.packageStatus?.scansTotal || 0;
    const currentAvailable = selectedUser.packageStatus?.scansLeft || 0;
    const newExpiry = calculateNewExpiryForScans(selectedUser);
    const currentExpiry = selectedUser.packageExpiresAt 
      ? new Date(selectedUser.packageExpiresAt.seconds * 1000)
      : null;
    
    const newTotal = currentTotal + (parseInt(addScansAmount) || 0);
    const willExtend = !currentExpiry || newExpiry > currentExpiry;

    return (
      <>
        <div className="modal-overlay" onClick={closeAddScansModal}></div>
        <div className="user-modal">
          <div className="modal-header">
            <h3>Scans Toevoegen</h3>
            <button className="modal-close" onClick={closeAddScansModal}>×</button>
          </div>
          
          <div className="modal-body">
            <div className="user-info-section">
              <div className="user-info-row">
                <span className="user-info-label">Gebruiker:</span>
                <span className="user-info-value">{selectedUser.name || selectedUser.email}</span>
              </div>
              <div className="user-info-row">
                <span className="user-info-label">Email:</span>
                <span className="user-info-value">{selectedUser.email}</span>
              </div>
            </div>

            <div className="package-info-section">
              <h4>Huidige Situatie</h4>
              <div className="package-stats">
                <div className="package-stat">
                  <span className="package-stat-label">Totaal Scans</span>
                  <span className="package-stat-value">{currentTotal}</span>
                </div>
                <div className="package-stat">
                  <span className="package-stat-label">Beschikbaar</span>
                  <span className="package-stat-value highlight">{currentAvailable}</span>
                </div>
              </div>
              
              {selectedUser.packageExpiresAt && (
                <div className="expiry-info">
                  Vervaldatum: <strong>{new Date(selectedUser.packageExpiresAt.seconds * 1000).toLocaleDateString('nl-NL')}</strong>
                </div>
              )}
            </div>

            <div className="form-section">
              <div className="form-group">
                <label>Aantal scans toevoegen:</label>
                <input
                  type="number"
                  min="1"
                  value={addScansAmount}
                  onChange={(e) => setAddScansAmount(e.target.value)}
                  placeholder="Bijv. 4, 8, 12"
                  autoFocus
                />
              </div>
            </div>

            {addScansAmount && parseInt(addScansAmount) > 0 && (
              <div className="package-info-section">
                <h4>Nieuwe Situatie</h4>
                <div className="package-stats">
                  <div className="package-stat">
                    <span className="package-stat-label">Totaal Scans</span>
                    <span className="package-stat-value">{newTotal}</span>
                  </div>
                  <div className="package-stat">
                    <span className="package-stat-label">Toevoeging</span>
                    <span className="package-stat-value highlight">+{addScansAmount}</span>
                  </div>
                </div>
                
                <div className={`expiry-info ${willExtend ? 'expiry-extended' : ''}`}>
                  Vervaldatum: <strong>{newExpiry.toLocaleDateString('nl-NL')}</strong>
                  {willExtend && currentExpiry && (
                    <div style={{ fontSize: '12px', marginTop: '5px', color: '#059669' }}>
                      ✓ Verlengd (was: {currentExpiry.toLocaleDateString('nl-NL')})
                    </div>
                  )}
                  {!willExtend && (
                    <div style={{ fontSize: '12px', marginTop: '5px', color: '#6b7280' }}>
                      (Blijft hetzelfde - huidige datum ligt verder)
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="modal-footer">
              <button 
                className="btn btn-outline"
                onClick={closeAddScansModal}
                disabled={loading}
              >
                Annuleren
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleAddScans}
                disabled={loading || !addScansAmount || parseInt(addScansAmount) <= 0}
              >
                {loading ? 'Bezig...' : 'Scans Toevoegen'}
              </button>
            </div>
          </div>
        </div>
      </>
    );
  };

  // Render Extend Modal
  const renderExtendModal = () => {
    if (!showExtendModal || !selectedUser) return null;

    const months = extendMonths === 'custom' ? parseInt(customMonths) || 0 : extendMonths;
    const currentExpiry = selectedUser.packageExpiresAt 
      ? new Date(selectedUser.packageExpiresAt.seconds * 1000)
      : null;
    const newExpiry = months > 0 ? calculateNewExpiryForExtend(selectedUser, months) : null;

    return (
      <>
        <div className="modal-overlay" onClick={closeExtendModal}></div>
        <div className="user-modal">
          <div className="modal-header">
            <h3>Geldigheidsduur Verlengen</h3>
            <button className="modal-close" onClick={closeExtendModal}>×</button>
          </div>
          
          <div className="modal-body">
            <div className="user-info-section">
              <div className="user-info-row">
                <span className="user-info-label">Gebruiker:</span>
                <span className="user-info-value">{selectedUser.name || selectedUser.email}</span>
              </div>
              <div className="user-info-row">
                <span className="user-info-label">Email:</span>
                <span className="user-info-value">{selectedUser.email}</span>
              </div>
            </div>

            <div className="package-info-section">
              <h4>Huidige Vervaldatum</h4>
              <div className="expiry-info">
                {currentExpiry 
                  ? <strong>{currentExpiry.toLocaleDateString('nl-NL')}</strong>
                  : <span style={{color: '#6b7280'}}>Nog niet ingesteld</span>
                }
              </div>
            </div>

            <div className="form-section">
              <h4>Verlengen met:</h4>
              
              <div className="extend-options">
                <label className="extend-option">
                  <input 
                    type="radio" 
                    name="extend" 
                    value="3"
                    checked={extendMonths === 3}
                    onChange={() => setExtendMonths(3)}
                  />
                  <span>3 maanden</span>
                </label>
                
                <label className="extend-option">
                  <input 
                    type="radio" 
                    name="extend" 
                    value="6"
                    checked={extendMonths === 6}
                    onChange={() => setExtendMonths(6)}
                  />
                  <span>6 maanden</span>
                </label>
                
                <label className="extend-option">
                  <input 
                    type="radio" 
                    name="extend" 
                    value="12"
                    checked={extendMonths === 12}
                    onChange={() => setExtendMonths(12)}
                  />
                  <span>1 jaar (12 maanden)</span>
                </label>
                
                <label className="extend-option">
                  <input 
                    type="radio" 
                    name="extend" 
                    value="custom"
                    checked={extendMonths === 'custom'}
                    onChange={() => setExtendMonths('custom')}
                  />
                  <span>Aangepast:</span>
                  <input
                    type="number"
                    min="1"
                    value={customMonths}
                    onChange={(e) => {
                      setCustomMonths(e.target.value);
                      setExtendMonths('custom');
                    }}
                    placeholder="# maanden"
                    disabled={extendMonths !== 'custom'}
                    style={{ marginLeft: '10px', width: '100px' }}
                  />
                </label>
              </div>
            </div>

            {newExpiry && (
              <div className="package-info-section">
                <h4>Nieuwe Vervaldatum</h4>
                <div className="expiry-info expiry-extended">
                  <strong>{newExpiry.toLocaleDateString('nl-NL')}</strong>
                  <div style={{ fontSize: '12px', marginTop: '5px', color: '#059669' }}>
                    ✓ Verlengd met {months} maanden
                    {currentExpiry && ` (vanaf ${currentExpiry.toLocaleDateString('nl-NL')})`}
                  </div>
                </div>
              </div>
            )}

            <div className="modal-footer">
              <button 
                className="btn btn-outline"
                onClick={closeExtendModal}
                disabled={loading}
              >
                Annuleren
              </button>
              <button 
                className="btn btn-secondary"
                onClick={handleExtend}
                disabled={loading || months <= 0}
              >
                {loading ? 'Bezig...' : 'Verlengen'}
              </button>
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="package-management">
      <div className="package-header">
        <h2>Scans Beheer</h2>
        {message.text && (
          <div className={`message message-${message.type}`}>
            {message.text}
          </div>
        )}
      </div>

      <div className="package-content">
        {renderOverview()}
        
        <div className="user-management" style={{ marginTop: '40px' }}>
          <div className="filters-section">
            <h3>Gebruikers Overzicht</h3>
            
            <div className="filters">
              <input
                type="text"
                placeholder="Zoek op naam..."
                value={filters.name}
                onChange={(e) => setFilters({...filters, name: e.target.value})}
              />
              <input
                type="text"
                placeholder="Zoek op email..."
                value={filters.email}
                onChange={(e) => setFilters({...filters, email: e.target.value})}
              />
              <select
                value={filters.status}
                onChange={(e) => setFilters({...filters, status: e.target.value})}
              >
                <option value="all">📊 Alle gebruikers</option>
                <option value="active">🟢 Actief</option>
                <option value="expiring">🟡 Bijna verlopen</option>
                <option value="expired">🔴 Verlopen</option>
                <option value="inactive">⚪ Inactief</option>
              </select>
              <button className="btn btn-primary btn-sm" onClick={handleApplyFilters}>
                Filteren
              </button>
              <button className="btn btn-outline btn-sm" onClick={handleResetFilters}>
                Reset
              </button>
            </div>
          </div>

          {renderUsersTable()}
        </div>
      </div>

      {/* Modals */}
      {renderAddScansModal()}
      {renderExtendModal()}
    </div>
  );
};

export default PackageManagement;
